import { useContext } from 'react';
import { useParams } from 'react-router';
import _ from 'lodash';

import { sectionTypes } from '../../../constants/fields';
import { mapModes } from '../../../constants/map';
import { MapContext } from '../../../contexts/MapContext';
import DropdownButton from '../../basics/DropdownButton';
import BetaBadge from '../../basics/BetaBadge';
import SectionsIcon from '../../icons/SectionsIcon';
import { getIconForSectionType } from '../../../utils/sections';
import ImportFieldsButton from './ImportFieldsButton';
import DropdownOption from '../../basics/DropdownOption';
import { uniqueName } from '../../../utils/utils';
import { captureEvent } from '../../../utils/analytics';
import Shortcut from '../../basics/Shortcut';
import { SHORTCUTS } from '../../../constants/shortcuts';

const shortcutsForType = {
  [sectionTypes.GRID]: SHORTCUTS.NEW_GRID.key,
  [sectionTypes.LINE]: SHORTCUTS.NEW_LINE.key,
  [sectionTypes.KEYLINE]: SHORTCUTS.NEW_KEYLINE.key,
};

const getSectionTypeOptions = (availableSectionTypes) =>
  availableSectionTypes.map((type) => {
    const icon = getIconForSectionType(type);
    return type === sectionTypes.IMPORTED
      ? {
          overrideComponent: (
            <ImportFieldsButton
              key="upload"
              render={(onClick) => (
                <DropdownOption
                  option={{ name: 'Upload', toggle: false, icon }}
                  onClickOverride={onClick}
                />
              )}
            />
          ),
        }
      : {
          type,
          key: type,
          name: (
            <div className="d-flex align-items-center">
              {_.capitalize(type)}
              {type === sectionTypes.KEYLINE && <BetaBadge />}
            </div>
          ),
          icon,
        };
  });

const CreateSectionButton = ({
  availableSectionTypes = Object.values(sectionTypes),
  initialSectionData = {},
  sourceFeatureType,
  ...buttonProps
}) => {
  const {
    fields: { addFieldSection, fieldSections },
    mapState: { setMapMode },
  } = useContext(MapContext);
  const { farmId } = useParams();

  const sectionTypeOptions = getSectionTypeOptions(availableSectionTypes);
  const onSelect = async ({ type }) => {
    if (type !== sectionTypes.IMPORTED) {
      const { id } = addFieldSection({
        type,
        farmId,
        name: uniqueName('Field', fieldSections),
        ...initialSectionData,
      });
      if (initialSectionData.fieldFeature) {
        if (type === sectionTypes.KEYLINE && !initialSectionData.rowFeature) {
          // We have an outline, but no row feature
          setMapMode(mapModes.DRAW, id, { keylineMode: true });
        } else {
          setMapMode(mapModes.SECTION_ENGAGED, id);
        }
      } else {
        setMapMode(mapModes.DRAW, id);
      }
    }
    captureEvent(`new-${type}-section`, { sourceFeatureType });
  };

  const selectedOption = (
    <span className="d-flex align-items-center">
      <SectionsIcon className="d-flex mr-2 align-items-center" />
      New Section
    </span>
  );

  return (
    <>
      <DropdownButton
        outline
        options={sectionTypeOptions}
        setOption={onSelect}
        selectedOption={selectedOption}
        {...buttonProps}
      />

      {/* Shortcuts */}
      {availableSectionTypes
        .filter((type) => type !== sectionTypes.IMPORTED)
        .map((type) => (
          <Shortcut
            key={type}
            shortcut={shortcutsForType[type]}
            handler={() => onSelect({ type })}
          />
        ))}
    </>
  );
};

export default CreateSectionButton;
